import { useCallback, useEffect, useState } from 'react';
import { toggleAllExpandedRowIds } from 'src/components/dispatch/expandAllRows';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

import { pruneExpandedRowIds } from './pruneExpandedRowIds';

/**
 * 車両行の展開 state。リセットと prune をここに閉じる。
 *
 * Presenter が useState と 2 つの useEffect を直接持つと、配線を忘れても
 * pruneExpandedRowIds の unit test は通ってしまう。hook にすることで
 * renderHook から「日付を変えると空になる」ことを直接テストできる
 * （P2c で useDisplayMetrics を hook にしたのと同じ方式）。
 */
export function useExpandedRowIds(params: {
  startOn: string;
  endOn: string;
  /** Map Presenter の契約に合わせて string。Container が String(...) で渡している */
  companyId: string;
  rowGroups: readonly VehicleRowGroup[];
}): readonly [
    ReadonlySet<string>,
    (rowId: string) => void,
    (expandableRowIds: readonly string[]) => void,
  ] {
  const { startOn, endOn, companyId, rowGroups } = params;
  const [expandedRowIds, setExpandedRowIds] = useState<ReadonlySet<string>>(() => new Set<string>());

  // /map/:startOn/:endOn は useParams なので日付を変えても再マウントされない。
  // 持ち越すと別日の同じ rowId が展開済みになる。
  useEffect(() => {
    setExpandedRowIds(new Set<string>());
  }, [startOn, endOn, companyId]);

  // 再取得・回転削除で消えた rowId を落とす
  useEffect(() => {
    setExpandedRowIds((prev) => pruneExpandedRowIds(prev, rowGroups));
  }, [rowGroups]);

  const toggleRowExpanded = useCallback((rowId: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  // 「すべて展開 / すべて閉じる」。対象行は画面に見えている行だけなので呼び出し側が渡す
  const toggleAllExpanded = useCallback((expandableRowIds: readonly string[]) => {
    setExpandedRowIds((prev) => toggleAllExpandedRowIds(expandableRowIds, prev));
  }, []);

  return [expandedRowIds, toggleRowExpanded, toggleAllExpanded] as const;
}
